import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import React, { ReactNode, useEffect, useState, useMemo, useRef } from "react";
import { useActividadesContext } from "../Hook/useActividades";
import HeaderApps from "./HeaderApps";
import UmmIAChatModal from "../../UmmIAChatAI/UmmIAChatModal";
import { ChatUIProvider, useChatUI } from "../../UmmIAChatAI/ChatUIContext";
import UmmIAAgentIcon from "../../UmmIAChatAI/svg/UmmIAAgentIcon.svg";
import { ProjectActionsButtons } from "./components/ProjectActionButtons";
import { useLocation } from "react-router-dom";
import { iDataProyectoPorUsuario } from "../../../interfaces/iDataProyectoPorUsuario";
import MarkDownEditor from "../../MarkDownText/MarkDownEditor";
import MarkDownText from "../../MarkDownText/MarkDownText";
import HeaderAppsMyProjects from "./components/HeaderAppsMyProjects";
import { useProjectsContext } from "../../../context/Projects/ProjectContext";

type ConnectedMarkDownEditorProps = {
  data: string;
  setEditedValue: (value: string) => void;
  img: string;
};

const ConnectedMarkDownEditor: React.FC<ConnectedMarkDownEditorProps> = ({
  data,
  setEditedValue,
  img,
}) => {
  const { setContextProvider, setDocApplier } = useChatUI();
  const latestValueRef = useRef(data);

  // Mantener referencia al texto actual
  useEffect(() => {
    latestValueRef.current = data;
  }, [data]);

  // Registrar cómo obtener el contexto y cómo aplicar cambios
  useEffect(() => {
    setContextProvider(() => {
      return latestValueRef.current || "";
    });

    setDocApplier((md: string) => {
      console.log("[ConnectedMarkDownEditor] docApplier llamado");
      setEditedValue(md);
    });
  }, [setContextProvider, setDocApplier, setEditedValue]);

  return (
    <MarkDownEditor
      data={data}
      setEditedValue={setEditedValue}
      img={img}
    />
  );
};

type LayoutProps = {
  children: ReactNode;
  title?: string;
  imageSrc: string;
  description: string;
  titleColor: string;
  backgroundColorImage: string;
  isButtonDisabled: boolean;
  onBack?: () => void;
  colorIconBack: string;
  onGuardarProyecto?: (
    proyectoId: string,
    usuarioId: string,
    resultadoId: string,
    proyectoObjPropuesta: iDataProyectoPorUsuario["propuesta"]
  ) => Promise<void> | void;
};

const CHAT_PANEL_WIDTH = 380;

type ProyectoNavegacion = {
  proyectoId: string;
  usuarioId: string;
  resultadoPropuestaId: string;
  propuesta: iDataProyectoPorUsuario["propuesta"];
};


const buildMarkdownFromPropuesta = (
  propuesta: iDataProyectoPorUsuario["propuesta"] | null | undefined
): string => {
  if (!propuesta) return "";

  const anyProp = propuesta as any;


  if (typeof anyProp.resultado === "string") {
    return anyProp.resultado;
  }

  //Caso Aula Activa 
  if ("plan_academico" in anyProp) {
    let md = `# Plan académico\n\n${anyProp.plan_academico || ""}\n\n`;

    if (Array.isArray(anyProp.temas) && anyProp.temas.length) {
      md += `## Temas\n\n`;
      anyProp.temas.forEach((t: any) => {
        md += `### Semana ${t.semana ?? ""} - ${t.sesion ?? ""}\n\n`;
        if (t.tema) {
          md += `**Tema:** ${t.tema}\n\n`;
        }
        if (Array.isArray(t.actividades) && t.actividades.length) {
          md += `**Actividades:**\n`;
          t.actividades.forEach((act: string) => {
            md += `- ${act}\n`;
          });
          md += `\n`;
        }
      });
    }

    return md.trim();
  }

  //  Caso ABP 
  if ("Pregunta_Guia" in anyProp && "Problematica" in anyProp) {
    const p = anyProp;
    let md = `# ${p.Titulo || "Propuesta ABP"}\n\n`;

    const section = (title: string, value?: string) =>
      value ? `## ${title}\n${value}\n\n` : "";

    md += section("Pregunta guía", p.Pregunta_Guia);
    md += section("Problemática", p.Problematica);
    md += section("Objetivos", p.Objetivos);
    md += section("Aprendizajes esperados", p.Aprendizajes_Esperados);
    md += section("Conceptos clave", p.Conceptos_Clave);
    md += section("Producto final", p.Producto_Final);
    md += section("Storytelling", p.Story_Telling);
    md += section("Lanzamiento", p.Lanzamiento);
    md += section("Indagación", p.Indagacion);
    md += section("Creación", p.Creacion);
    md += section("Beneficiario / Exhibición", p.Beneficiario_Exhibicion);
    md += section("Evaluación", p.Evaluacion);
    md += section("Bibliografía", p.Bibliografia);

    return md.trim();
  }

  return "```json\n" + JSON.stringify(propuesta, null, 2) + "\n```";
};

const LayoutBody: React.FC<LayoutProps> = ({
  onBack,
  imageSrc,
  title,
  description,
  backgroundColorImage,
  children,
  titleColor,
  isButtonDisabled,
  colorIconBack,
  onGuardarProyecto,
}) => {
  const { editMode, setRespuestaEditable, respuestaImg } = useActividadesContext();
  const { isChatOpen, openChat, closeChat } = useChatUI();
  const { actualizarPropuestaProyecto } = useProjectsContext();

  // Obtener data del proyecto desde la ruta
  const location = useLocation();
  const proyecto = (location.state as any)?.proyecto as
    | ProyectoNavegacion
    | undefined;

  // Estado local de la propuesta
  const [proyectoLocal, setProyectoLocal] =
    useState<iDataProyectoPorUsuario["propuesta"] | null>(null);

  // Flag de cambios sin guardar
  const [hasUnsaved, setHasUnsaved] = useState(false);

  // Inicializar estado local y markdown cuando llega el proyecto
  useEffect(() => {
    if (proyecto?.propuesta) {
      setProyectoLocal(proyecto.propuesta);

      const initialMarkdown = buildMarkdownFromPropuesta(proyecto.propuesta);
      setRespuestaEditable(initialMarkdown);
    }
  }, [proyecto, setRespuestaEditable]);

  // Manejo de apertura/cierre del chat según editMode
  const hasForcedOpenRef = useRef(false);

  useEffect(() => {
    if (!hasForcedOpenRef.current) {
      openChat();
      hasForcedOpenRef.current = true;
    }
  }, [openChat]);



  // Cada vez que se edita el texto en el editor
  const handleResultadoChange = (nuevoValor: string) => {
    setRespuestaEditable(nuevoValor);

    // Actualiza el proyecto local
    setProyectoLocal((prev: any) =>
      prev ? { ...prev, resultado: nuevoValor } : ({ resultado: nuevoValor } as any)
    );

    setHasUnsaved(true);
  };

  const handleSaveClick = async () => {
    if (!onGuardarProyecto) {
      console.warn(
        "EditProjectLayout: onGuardarProyecto no fue proporcionado. No se realizará guardado."
      );
      return;
    }
    if (!proyecto || !proyectoLocal) {
      console.warn(
        "EditProjectLayout: proyecto o proyectoLocal no están disponibles para guardar."
      );
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });

    await onGuardarProyecto(
      proyecto.proyectoId,
      proyecto.usuarioId,
      proyecto.resultadoPropuestaId,
      proyectoLocal
    );

    actualizarPropuestaProyecto(proyecto.proyectoId, proyectoLocal as any);

    setHasUnsaved(false);
  };

  // Markdown que se mostrará actualmente en el editor / vista
  const markdownActual = useMemo(() => {
    return buildMarkdownFromPropuesta(proyectoLocal ?? proyecto?.propuesta);
  }, [proyectoLocal, proyecto]);


  return (
    <Box
      sx={{
        display: "block",
        width: "100%",
        minHeight: "100dvh",
        bgcolor: "background.default",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      {/* Columna izquierda: chat */}
      <Box
        component="aside"
        sx={{
          position: "fixed",
          left: 70,
          top: 70,
          height: "93dvh",
          width: isChatOpen ? CHAT_PANEL_WIDTH : 0,
          bgcolor: isChatOpen ? "background.paper" : "transparent",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "hidden",
          zIndex: 10,
          borderRight: isChatOpen ? "1px solid #e0e0e0" : "none",
        }}
      >
        <Box
          sx={{
            p: isChatOpen ? 1.25 : 0,
            height: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            transition: "padding 200ms ease",
          }}
        >
          <UmmIAChatModal
            open={isChatOpen}
            onClose={closeChat}
            onCollapse={closeChat}
            mode="inline"
            styleOverrides={{
              width: "100%",
              height: "100%",
              borderRadius: "20px",
            }}
          />
        </Box>
      </Box>

      {/* Botón flotante para abrir el chat cuando está cerrado */}
      {!editMode && !isChatOpen && (
        <Tooltip
          title="Abrir UmmIA Agent"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#1D3D4D",
                color: "#FFF",
                fontFamily: "Montserrat",
                fontSize: "0.875rem",
                padding: "8px 12px",
                borderRadius: "8px",
              },
            },
            arrow: { sx: { color: "#1D3D4D" } },
          }}
        >
          <IconButton
            onClick={openChat}
            aria-label="Abrir UmmIA Agent"
            sx={{
              position: "fixed",
              left: 90,
              bgcolor: "#4C7592",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 11,
              width: 36,
              height: 36,
              color: "#FFF",
              borderRadius: "12px",
              boxShadow: 3,
              "&:hover": { bgcolor: "#4C7592" },
            }}
          >
            <Box
              component="img"
              src={UmmIAAgentIcon}
              alt="Abrir chat"
              sx={{ width: 35, height: 35, display: "block" }}
            />
          </IconButton>
        </Tooltip>
      )}

      {/* Columna derecha: contenido principal */}
      <Box
        component="main"
        sx={{
          ml: isChatOpen ? `${CHAT_PANEL_WIDTH}px` : 5,
          transition: "margin-left 200ms ease",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <Box sx={{ flexShrink: 0 }}>
          <HeaderAppsMyProjects
            imageSrc={imageSrc}
            title={title}
            titleColor={titleColor}
            description={description}
            backgroundColorImage={backgroundColorImage}
            onBack={onBack}
            colorIconBack={colorIconBack}
          />
        </Box>

        <Grid item xs={12} sx={{ width: "100%" }}>
          {editMode ? (
            <Grid>
              <MarkDownText data={markdownActual} img={respuestaImg} />
            </Grid>
          ) : (
            <>
              <ConnectedMarkDownEditor
                data={markdownActual}
                setEditedValue={handleResultadoChange}
                img={""}
              />
            </>
          )}

          {/* componente para botones*/}
          <ProjectActionsButtons
            onSaveProject={handleSaveClick}
            data={markdownActual}
            resultadoPropuestaId={proyecto?.resultadoPropuestaId}
          />
        </Grid>
      </Box>
    </Box>
  );
};

const EditProjectLayout: React.FC<LayoutProps> = (props) => (
  <ChatUIProvider>
    <LayoutBody {...props} />
  </ChatUIProvider>
);

export default EditProjectLayout;
